/**
 * SR-NET-05 교사 학교망 진단 계약(C-07 · Bearer · 교사 전용 — 제어 평면 NetcheckController):
 *   POST /api/courses/{c}/teacher/netcheck         → { wsUrl, token, expiresAt, plan:{pings, downKb, upKb, timeoutMs}, thresholds:{…} }
 *   GET  /api/courses/{c}/teacher/netcheck/ping    → 204(HTTPS 왕복)
 *   POST /api/courses/{c}/teacher/netcheck/result  { wss, rttMs?, downKbps?, upKbps?, httpsRttMs?, port443? } → 202 { verdict }
 * 진단 토큰은 기관 가명 키만 싣는다(pid · 코스 없음) — 결과 텔레메트리도 기관 단위 · 비식별.
 */
import { useMemo } from "react";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import { DEFAULT_THRESHOLDS } from "../lib/measure";
import type { NetcheckResult, NetcheckSession, Thresholds, Verdict } from "../types";

export interface NetcheckApi {
  session(): Promise<NetcheckSession>;
  ping(): Promise<void>;
  /** 결과 보고 — 서버가 다시 매긴 판정(못 보내면 null) */
  report(r: NetcheckResult): Promise<Verdict | null>;
}

const num = (v: unknown, d: number) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : d;

export function readSession(v: unknown): NetcheckSession {
  const o = (v ?? {}) as Record<string, unknown>;
  if (typeof o.wsUrl !== "string" || typeof o.token !== "string")
    throw new Error("netcheck session");
  const p = (o.plan ?? {}) as Record<string, unknown>;
  const t = (o.thresholds ?? {}) as Record<string, unknown>;
  const th = Object.fromEntries(
    Object.entries(DEFAULT_THRESHOLDS).map(([k, d]) => [k, num(t[k], d)]),
  ) as unknown as Thresholds;
  return {
    wsUrl: o.wsUrl,
    token: o.token,
    plan: {
      pings: Math.min(30, num(p.pings, 10)),
      downKb: Math.min(1024, num(p.downKb, 512)),
      upKb: Math.min(256, num(p.upKb, 96)),
      timeoutMs: Math.min(30_000, num(p.timeoutMs, 10_000)),
    },
    thresholds: th,
  };
}

export function createNetcheckApi(request: Requester, courseId: string): NetcheckApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/netcheck`;
  return {
    session: async () => readSession(await request<unknown>(c, { method: "POST" })),
    ping: async () => {
      await request<unknown>(`${c}/ping`, { cache: "no-store" });
    },
    async report(r) {
      try {
        const body: Record<string, unknown> = { wss: r.wss, port443: r.port443 };
        if (r.rttMs != null) body.rttMs = r.rttMs;
        if (r.downKbps != null) body.downKbps = r.downKbps;
        if (r.upKbps != null) body.upKbps = r.upKbps;
        if (r.httpsRttMs != null) body.httpsRttMs = r.httpsRttMs;
        const v = await request<{ verdict?: unknown }>(`${c}/result`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        return v?.verdict === "pass" || v?.verdict === "warn" || v?.verdict === "fail"
          ? v.verdict
          : null;
      } catch {
        return null;
      }
    },
  };
}

/** 교사 세션에서만 */
export function useNetcheckApi(): NetcheckApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createNetcheckApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
